// ATE-CR-117
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useTenantContext } from "@/context/TenantContext";
import type { Tenant } from "@/types/tenants";

import "./TenantSwitcher.css";

const HQ_LABEL = "Atelier Admin" as const;

// ATE-CR-117
function getScopeLabel(scope: ReturnType<typeof useTenantContext>["scope"]): string {
  return scope.mode === "hq" ? HQ_LABEL : scope.tenant.name;
}

// ATE-CR-117
export function TenantSwitcher(): JSX.Element {
  const { scope, setScope, availableTenants, isLoadingTenants } =
    useTenantContext();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const triggerId = `${uid}-trigger`;

  // ATE-CR-117 — bug-fix hotfix:
  // The dropdown panel is rendered via React Portal into `document.body` so
  // ancestor `overflow: hidden` on `.hub-sidebar` cannot clip it. We compute
  // the panel's viewport coordinates from the trigger's bounding rect and
  // re-measure on scroll/resize so the panel tracks the trigger correctly.
  const [panelPosition, setPanelPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return;
    }
    function recompute(): void {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPanelPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return (): void => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [isOpen]);

  // ATE-CR-117
  // Click-outside: attach mousedown listener to document while open.
  // The panel is portaled outside containerRef, so we also check panelRef
  // to avoid auto-closing when the user clicks inside the dropdown itself.
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: MouseEvent): void => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target) ?? false;
      const insidePanel = panelRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insidePanel) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen]);

  // ATE-CR-117
  // Auto-focus the search input when the panel opens.
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setActiveIndex(0);
      setQuery("");
    }
  }, [isOpen]);

  // ATE-CR-117
  // Filter tenants by query (HQ row is always pinned; not subject to filtering).
  const filteredTenants = useMemo<Tenant[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableTenants;
    return availableTenants.filter(
      (t) =>
        t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q),
    );
  }, [query, availableTenants]);

  // Items: index 0 = HQ, index 1..N = filteredTenants[N-1]
  const totalItems = 1 + filteredTenants.length;

  // ATE-CR-117
  function activateIndex(idx: number): void {
    if (idx === 0) {
      setScope({ mode: "hq" });
    } else {
      setScope({ mode: "tenant", tenant: filteredTenants[idx - 1] });
    }
    setIsOpen(false);
  }

  // ATE-CR-117
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, totalItems - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      activateIndex(activeIndex);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  }

  // ATE-CR-117
  function getOptionId(idx: number): string {
    return idx === 0 ? `${listboxId}-hq` : `${listboxId}-tenant-${filteredTenants[idx - 1].id}`;
  }

  const activeDescendant = isOpen ? getOptionId(activeIndex) : undefined;

  return (
    <div ref={containerRef} className="tenant-switcher">
      {/* Trigger button — role="combobox" per WAI-ARIA combobox pattern */}
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-label="Switch tenant scope"
        className="tenant-switcher__trigger"
        onClick={(): void => setIsOpen((prev) => !prev)}
        disabled={isLoadingTenants}
      >
        <span className="tenant-switcher__label">
          {isLoadingTenants ? "Loading…" : getScopeLabel(scope)}
        </span>
        <span className="tenant-switcher__chevron" aria-hidden="true">
          {isOpen ? "▲" : "▼"}
        </span>
      </button>

      {/* ATE-CR-117 — Dropdown panel portaled to document.body so the
          sidebar's `overflow: hidden` cannot clip it. Position is computed
          from the trigger's bounding rect (recomputed on scroll/resize). */}
      {isOpen && panelPosition && createPortal(
        <div
          ref={panelRef}
          className="tenant-switcher__panel"
          style={{
            top: panelPosition.top,
            left: panelPosition.left,
            width: panelPosition.width,
          }}
        >
          {/* Search input */}
          <input
            ref={inputRef}
            type="search"
            role="searchbox"
            aria-label="Search tenants"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeDescendant}
            className="tenant-switcher__search"
            placeholder="Search…"
            value={query}
            onChange={(e): void => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
          />

          {/* Listbox */}
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Tenant scope options"
            className="tenant-switcher__listbox"
          >
            {/* HQ option — pinned first */}
            <li
              id={getOptionId(0)}
              role="option"
              aria-selected={activeIndex === 0}
              className={
                "tenant-switcher__option tenant-switcher__option--hq" +
                (activeIndex === 0 ? " tenant-switcher__option--active" : "")
              }
              onClick={(): void => {
                activateIndex(0);
              }}
            >
              {HQ_LABEL}
            </li>

            {/* Tenant options */}
            {filteredTenants.map((tenant, i) => {
              const idx = i + 1;
              return (
                <li
                  key={tenant.id}
                  id={getOptionId(idx)}
                  role="option"
                  aria-selected={activeIndex === idx}
                  className={
                    "tenant-switcher__option" +
                    (activeIndex === idx
                      ? " tenant-switcher__option--active"
                      : "")
                  }
                  onClick={(): void => {
                    activateIndex(idx);
                  }}
                >
                  {tenant.name}
                </li>
              );
            })}

            {/* Empty state */}
            {filteredTenants.length === 0 && query.trim() !== "" && (
              <li
                role="option"
                aria-selected={false}
                aria-disabled="true"
                className="tenant-switcher__option tenant-switcher__option--empty"
              >
                No tenants match "{query}"
              </li>
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
